import React, { useEffect, useRef, useState } from 'react';
import { UploadCloud, X, ClipboardPaste } from 'lucide-react';

/**
 * Reusable image uploader with:
 *  - drag & drop
 *  - click to browse
 *  - paste from clipboard (Ctrl/Cmd + V)
 *  - live preview + remove
 *
 * Controlled: pass `file` (File | null) and `onFile(file | null)`.
 * Validation is left to the parent (called via onFile).
 *
 * `previewUrl` (optional): a fallback image URL shown when no File is selected
 * (e.g. an existing server image while editing). A picked File always takes
 * priority over it; removing the File falls back to this URL.
 */
const ImageDropzone = ({ file, onFile, accept = 'image/*', disabled = false, previewUrl = null }) => {
    const [dragOver, setDragOver] = useState(false);
    const [filePreview, setFilePreview] = useState(null);
    const inputRef = useRef(null);

    // Build / clean up the object-url preview from the current file
    useEffect(() => {
        if (file instanceof File) {
            const url = URL.createObjectURL(file);
            setFilePreview(url);
            return () => URL.revokeObjectURL(url);
        }
        setFilePreview(null);
    }, [file]);

    // Paste support — grab the first image off the clipboard
    useEffect(() => {
        if (disabled) return;
        const onPaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
                if (item.type && item.type.startsWith('image/')) {
                    const f = item.getAsFile();
                    if (f) { onFile(f); break; }
                }
            }
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, [disabled, onFile]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        const f = e.dataTransfer?.files?.[0];
        if (f) onFile(f);
    };

    // A freshly-picked File previews over any fallback URL
    const hasFile = file instanceof File;
    const preview = filePreview || previewUrl;

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !disabled && inputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-all
                ${dragOver ? 'border-main-blue bg-main-th' : 'border-main-border bg-main-bg hover:border-main-blue/50'}
                ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                disabled={disabled}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
            />

            {preview ? (
                <div className="relative">
                    <img
                        src={preview}
                        alt="preview"
                        className="w-20 h-20 rounded-xl object-cover border border-main-border shadow-sm"
                    />
                    {hasFile && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onFile(null); }}
                            className="absolute -top-2 -right-2 bg-main-delete text-white rounded-full p-1 shadow-md hover:bg-main-delete/90"
                            title="Remove image"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
            ) : (
                <div className="w-12 h-12 rounded-xl bg-main-th text-main-blue flex items-center justify-center">
                    <UploadCloud size={22} />
                </div>
            )}

            <div className="text-xs text-main-subfont leading-relaxed">
                {preview ? (
                    <span className="font-medium text-main-blue">Click to replace image</span>
                ) : (
                    <span>
                        <span className="font-medium text-main-font">Drag &amp; drop</span>, click to browse, or{' '}
                        <span className="inline-flex items-center gap-1 align-middle">
                            <ClipboardPaste size={12} /> paste
                        </span>
                    </span>
                )}
            </div>
        </div>
    );
};

export default ImageDropzone;
